import { useMemo } from 'react'

import { Body2 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { IconButton } from '@ui/components/Button'
import { Divider } from '@ui/components/Divider'
import { ListItem } from '@ui/components/List'
import { PopoverList } from '@ui/components/Popover'
import { Loader } from '@ui/components/Spinner/Loader'
import { Tooltip } from '@ui/components/Tooltip'
import { TourTarget } from '@ui/components/Tourtip/TourTarget'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { createValidIcon } from '@ui/icons/utils/type-helper'

import { Box } from '@shared/components/Box'

import type { WalletListView } from '../wallets.types'
import {
  WalletCryptoGroupIcon,
  WalletCurrencyGroupIcon,
  WalletCurrencyIcon,
} from './WalletCurrencyIcon'

import styles from './WalletListSection.module.scss'

interface WalletListSectionProps {
  walletList: WalletListView
}

const AddCryptoIcon = createValidIcon(WalletCryptoGroupIcon)

export const WalletListSection: React.FC<WalletListSectionProps> = ({ walletList }) => {
  const { totalBalance, addCurrency, addCrypto } = walletList
  const hasSingleWallet = walletList.items.length === 1
  const showAddCryptoDivider = !hasSingleWallet || !!addCurrency
  const walletIcons = useMemo(
    () =>
      Object.fromEntries(
        walletList.items.map((wallet) => [
          wallet.id,
          createValidIcon(() => <WalletCurrencyIcon currencyCode={wallet.currencyCode} />),
        ])
      ),
    [walletList.items]
  )
  const isAddCurrencyInteractionDisabled = !!addCurrency?.disabled || !!addCurrency?.loading
  const AddCurrencyIcon = useMemo(
    () =>
      createValidIcon(() => <WalletCurrencyGroupIcon currencies={addCurrency?.currencies ?? []} />),
    [addCurrency?.currencies]
  )

  return (
    <section className={styles.section} aria-label={totalBalance.label}>
      <Box flex direction='column' gap='x16'>
        {!hasSingleWallet && (
          <Box flex align='center' fullWidth p='x8' gap='x12' minHeight={56}>
            <WalletCurrencyGroupIcon currencies={totalBalance.currencies} />
            <Box flex flexItem='1' direction='column' align='flex-start' minWidth={0}>
              <Box flex align='center' maxWidth='100%' gap='x4' wrap>
                <Body2 medium color='text-high-em'>
                  {totalBalance.label}
                </Body2>
                {totalBalance.isLoading ? (
                  <Loader size={20} />
                ) : totalBalance.errorMessage ? (
                  <Body2 color='text-low-em'>{totalBalance.errorMessage}</Body2>
                ) : (
                  <Body2 medium color='text-high-em' sensitive>
                    {totalBalance.value}
                  </Body2>
                )}
                <Tooltip content={totalBalance.information} direction='bottom'>
                  <IconButton.Ghost
                    icon={InfoIcon}
                    size='xs'
                    aria-label={totalBalance.information}
                  />
                </Tooltip>
              </Box>
              <Body2 color='text-low-em'>{totalBalance.subtitle}</Body2>
            </Box>
          </Box>
        )}

        {walletList.items.length > 1 && (
          <>
            <Divider.Horizontal />
            <PopoverList>
              {walletList.items.map((wallet) => (
                <ListItem
                  key={wallet.id}
                  as='button'
                  size='md'
                  aria-current={wallet.isCurrent || undefined}
                  onClick={wallet.onSelect}
                  startSlot={{ icon: walletIcons[wallet.id], size: 'x40' }}
                  endSlot={{
                    icon: { icon: { icon: ArrowIcon.Right, color: 'text-med-em' } },
                  }}
                >
                  <Box flex flexItem='1' direction='column' align='flex-start' minWidth={0}>
                    <Box flex align='center' maxWidth='100%' minWidth={0} gap='x4'>
                      <Body2 medium color='text-high-em' sensitive>
                        {wallet.balance}
                      </Body2>
                      {wallet.isCurrent && (
                        <Badge size='xxs' type='black' contrast='high'>
                          {walletList.currentLabel}
                        </Badge>
                      )}
                    </Box>
                    <Body2 color='text-low-em'>{wallet.currencyName}</Body2>
                  </Box>
                </ListItem>
              ))}
            </PopoverList>
          </>
        )}

        {addCurrency && (
          <>
            {!hasSingleWallet && <Divider.Horizontal />}
            <Tooltip
              content={addCurrency.disabledMessage}
              disabled={
                !addCurrency.disabled || !!addCurrency.loading || !addCurrency.disabledMessage
              }
              fullWidth
            >
              <TourTarget target='wallets.add'>
                <ListItem
                  as='button'
                  size='md'
                  title={addCurrency.label}
                  subtitle={addCurrency.subtitle}
                  startSlot={{ icon: AddCurrencyIcon, size: 'x40' }}
                  endSlot={{ icon: { icon: ArrowIcon.Right } }}
                  disabled={isAddCurrencyInteractionDisabled}
                  aria-busy={addCurrency.loading || undefined}
                  onClick={addCurrency.onSelect}
                />
              </TourTarget>
            </Tooltip>
          </>
        )}
      </Box>

      {addCrypto && (
        <Box
          pt={showAddCryptoDivider ? 'x8' : undefined}
          mt={showAddCryptoDivider ? 'x8' : undefined}
          borderTop={showAddCryptoDivider ? 'outline-low-em' : undefined}
        >
          <ListItem
            as='button'
            size='md'
            title={addCrypto.label}
            subtitle={addCrypto.subtitle}
            startSlot={{ icon: AddCryptoIcon, size: 'x40' }}
            endSlot={{ icon: { icon: ArrowIcon.Right } }}
            disabled={addCrypto.disabled}
            onClick={addCrypto.onSelect}
          />
        </Box>
      )}
    </section>
  )
}
